import { useEffect, useRef } from 'react';
import { Stack, TextField } from '@mui/material';

const codeInputSx = {
  '& input': { textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' },
  '& .MuiFilledInput-root': {
    bgcolor: 'white',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
    '&:hover': { bgcolor: 'white' },
    '&.Mui-focused': { bgcolor: 'white' },
  },
};

export function VerificationCodeInput({
  isLoading,
  reset,
  callback,
  code,
  setCode,
}: {
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  callback?: (code: string) => void;
  reset?: boolean;
  isLoading?: boolean;
}) {
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const resetCode = () => {
    inputRefs.forEach((ref) => {
      if (ref.current) ref.current.value = '';
    });
    const firstInput = inputRefs[0].current;
    if (firstInput) firstInput.focus();
    setCode('');
  };

  useEffect(() => {
    if (code.length === 6) {
      if (typeof callback === 'function') callback(code);
      resetCode();
    }
  }, [code]); //eslint-disable-line

  useEffect(() => {
    resetCode();
  }, [reset]); //eslint-disable-line

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) {
    const input = e.currentTarget;
    const previousInput = inputRefs[index - 1]?.current;

    if ((e.keyCode === 8 || e.keyCode === 46) && input.value === '') {
      if (previousInput) {
        previousInput.focus();
      }
    }
  }

  function handleInput(
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) {
    const input = e.currentTarget;
    const nextInput = inputRefs[index + 1]?.current;

    if (input.value.length > 1) {
      input.value = input.value[0];
    }

    if (input.value && nextInput) {
      nextInput.focus();
    }

    setCode((prev) => {
      const newCode = prev.split('');
      newCode[index] = input.value;
      return newCode.join('');
    });
  }

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.preventDefault();
    const data = e.clipboardData.getData('Text').trim();
    for (let i = 0; i < data.length; i++) {
      const inputRef = inputRefs[i];
      if (inputRef?.current) {
        inputRef.current.value = data[i];
        setCode((prev) => prev + data[i]);
        inputRef.current.focus();
      }
    }
  };

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.target.select();
  }

  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <TextField
          variant="filled"
          sx={codeInputSx}
          className="no-arrows"
          inputMode="numeric"
          key={index}
          slotProps={{
            htmlInput: {
              maxLength: 1,
              inputMode: 'numeric',
              onFocus: handleFocus,
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleKeyDown(e, index),
              onChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleInput(e, index),
              onPaste: handlePaste,
            },
          }}
          inputRef={inputRefs[index]}
          autoFocus={index === 0}
          disabled={isLoading}
        />
      ))}
    </Stack>
  );
}
